import {observer} from "mobx-react";
import styles from "./styles/NewsBlock.module.scss"
import BlockTitle from "../_BlockTitle";
import React, {useRef, useState} from "react";
import {CSSTransition} from "react-transition-group";
import {IReactiveNewsContentItem, ReactiveContentItem} from "../../../schoolfeed/SchoolFeedContentItem";
import classNames from "classnames";
import {EditDropDownItem} from "../EditDropDown";
import FontAwesome from "../../../utilities/FontAwesome";
import {
    SupportedContentItemTypes,
} from "../../../schoolfeed/PostManager/SchoolFeedPostManager";
import {useCreateContentItem} from "../../../../hooks/useCreateContentItem";
import NewsSelector from "./NewsSelector";
import {Toggle} from "../../SchoolBlocks/EditBlockComponents/EditBlockInputs/Toggle";
import {ITerraceProps} from "../Terrace";
import {useStores} from "../../../../stores/useStores";
import {useSchoolFeedInfiniteScroll} from "../../../schoolfeed/hooks/useSchoolFeedInfiniteScroll";
import {FetchMoreNewsButton} from "./FetchMoreNewsButton";

type IReactiveNewsTerraceObj = Omit<INewsTerrace, "content_data"> & (
    {
        content_data: {
            [org_id: string]: {
                title: string,
                items: IReactiveNewsContentItem[],
                page_size: number,
                total: number,
            }
        },
    })

interface NewsBlockProps extends ITerraceProps {
    blockObj: IReactiveNewsTerraceObj,
}

const visibilityTransitionClassNames = {
    enter: styles.transitionEnter,
    appear: styles.transitionAppear,
    enterActive: styles.transitionEnterActive,
    appearActive: styles.transitionAppearActive,
    enterDone: styles.transitionEnterDone,
    appearDone: styles.transitionAppearDone,
    exit: styles.transitionExit,
    exitActive: styles.transitionExitActive,
    exitDone: styles.transitionExitDone,
};

const NewsBlock = observer((props: NewsBlockProps) => {

    const {organizationStore, sidebarStore, userStore} = useStores();
    const transitionRef = useRef(null);

    const {handleSave} = useCreateContentItem();

    const showDistrictOnSchoolSite = organizationStore.currentOrganization.type === 'school' && organizationStore.organization.district
    const contentDataKeys = Array.from(Object.keys(props.blockObj.content_data));
    const primaryOrgId = contentDataKeys[0];
    const secondaryOrgId = contentDataKeys.length > 1 ? contentDataKeys[1] : null;

    const [currentOrgId, setCurrentOrgId] = useState<string>(() => {
        if (contentDataKeys.length === 2) {
            if (props.blockObj.content_data[primaryOrgId].items.length > 0) {
                return primaryOrgId;
            } else {
                return secondaryOrgId as string;
            }
        } else {
            return primaryOrgId;
        }
    });

    const newsOptions = Object.entries(props.blockObj.content_data).map(([org_id, data]) => {
        return ({
            value: org_id,
            label: data.title,
        })
    });

    const onClick = () => {
        sidebarStore.setSidebar(
            {
                view: "SchoolFeedPost",
                sbtype: SupportedContentItemTypes.NEWS,
                handleSave: handleSave,
            }
        )
    }

    const newsBlockDropDownItems: EditDropDownItem[] = [
        {
            title: 'Add News Post',
            icon: <FontAwesome prefix={'fas'} fixedWidth className="fa-plus"/>,
            onClick: onClick,
        },
    ]

    const newsTerraceTitle = () => {
        if (organizationStore.organization.type === 'district' || showDistrictOnSchoolSite) {
            return 'District News'
        } else if (organizationStore.organization.type === 'school') {
            return 'School News'
        }
    }

    const titleandEditPencilClassName = classNames({
        [styles.titleAndEditPencil]: organizationStore.currentOrganization.type !== 'district',
    })

    const customTitleComponent = currentOrgId === organizationStore.currentOrganization.id ? undefined
        : <div className={styles.titleContainer}>
            <div className={`${styles.editableTitle} ${styles.editableTitleParent}`}>
                <h2>
                    {newsTerraceTitle()}
                </h2>
            </div>
        </div>

    const {
        contentItems: primaryOrgContentItems,
        totalContentItems: primaryOrgTotalContentItems,
        getItemAtIndex: primaryOrgGetItemAtIndex,
        showViewMore: primaryOrgShowViewMore,
        size: primaryOrgSize,
        setSize: primaryOrgSetSize,
        mutate: primaryOrgMutate,
        isLoading: primaryOrgIsLoading,
    } = useSchoolFeedInfiniteScroll(
        "news",
        {
            organization_pk: primaryOrgId,
            page_size: props.blockObj.content_data[primaryOrgId].page_size,
            news_or_third_party_media: true,
            published: !userStore.isEditor,
            organization_id: primaryOrgId,
            expand: "organization,discussion_prompt",
        },
        {
            contentItems: props.blockObj.content_data[primaryOrgId].items,
            total: props.blockObj.content_data[primaryOrgId].total,
        }
    )

    const {
        contentItems: secondaryOrgContentItems,
        totalContentItems: secondaryOrgTotalContentItems,
        getItemAtIndex: secondaryOrgGetItemAtIndex,
        showViewMore: secondaryOrgShowViewMore,
        size: secondaryOrgSize,
        setSize: secondaryOrgSetSize,
        mutate: secondaryOrgMutate,
        isLoading: secondaryOrgIsLoading,
    } = useSchoolFeedInfiniteScroll(
        "news",
        {
            organization_pk: secondaryOrgId,
            page_size: secondaryOrgId ? props.blockObj.content_data[secondaryOrgId].page_size : 0,
            news_or_third_party_media: true,
            published: !userStore.isEditor,
            organization_id: secondaryOrgId,
            expand: "organization",
        },
        {
            contentItems: secondaryOrgId ? props.blockObj.content_data[secondaryOrgId].items : [],
            total: secondaryOrgId ? props.blockObj.content_data[secondaryOrgId].total : 0,
        }
    )

    const isPrimaryOrg = currentOrgId === primaryOrgId;
    const contentItems = isPrimaryOrg ? primaryOrgContentItems : secondaryOrgContentItems;
    const totalContentItems = isPrimaryOrg ? primaryOrgTotalContentItems : secondaryOrgTotalContentItems;
    const getItemAtIndex = isPrimaryOrg ? primaryOrgGetItemAtIndex : secondaryOrgGetItemAtIndex;
    const showViewMore = isPrimaryOrg ? primaryOrgShowViewMore : secondaryOrgShowViewMore;
    const size = isPrimaryOrg ? primaryOrgSize : secondaryOrgSize;
    const setSize = isPrimaryOrg ? primaryOrgSetSize : secondaryOrgSetSize;
    const mutate = isPrimaryOrg ? primaryOrgMutate : secondaryOrgMutate;
    const isLoading = isPrimaryOrg ? primaryOrgIsLoading : secondaryOrgIsLoading;

    return (
        <div className={styles.newsBlockContainer}>
            <div id={'blockTitle'} className={titleandEditPencilClassName}>
                <BlockTitle blockObj={props.blockObj}
                            aboveTheFold={props.aboveTheFold}
                            blockTitleContainerClassName={styles.blockTitleContainerClassName}
                            dropDownItems={currentOrgId === organizationStore.currentOrganization.id ?
                                newsBlockDropDownItems : undefined}
                            customTitleComponent={customTitleComponent}
                            showViewMore={false}
                />
                {(organizationStore.currentOrganization.type !== 'district' || showDistrictOnSchoolSite) && <Toggle
                    value={currentOrgId}
                    onChange={setCurrentOrgId}
                    options={newsOptions}
                    className={styles.toggleClassName}
                    id={String(props.blockObj.id)}
                />}
            </div>
            <div className={styles.transitionWrapper}>
                <CSSTransition nodeRef={transitionRef} key={currentOrgId} appear in={true}
                               classNames={visibilityTransitionClassNames}
                               timeout={250}>
                    <div ref={transitionRef} className={styles.transitionContainer}>
                        {contentItems.length === 0 ?
                            <div className={styles.noContent}>
                                No content to display, please try another filter.
                            </div> :
                            <NewsSelector
                                contentItems={contentItems as IReactiveNewsContentItem[]}
                                aboveTheFold={props.aboveTheFold}
                                viewMoreButton={showViewMore ?
                                    <FetchMoreNewsButton
                                        onClick={async () => await setSize(size + 1)}
                                        isLoading={isLoading}
                                    /> : undefined}
                                getItemAtIndex={getItemAtIndex}
                                totalItemCount={totalContentItems}
                                handleDelete={async () => await mutate()}
                            />
                        }
                    </div>
                </CSSTransition>
            </div>
        </div>
    );
})

export default NewsBlock;
